import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import BakeryDiningRoundedIcon from '@mui/icons-material/BakeryDiningRounded';
import CookieRoundedIcon from '@mui/icons-material/CookieRounded';
import FastfoodRoundedIcon from '@mui/icons-material/FastfoodRounded';
import LocalDiningRoundedIcon from '@mui/icons-material/LocalDiningRounded';
import LunchDiningRoundedIcon from '@mui/icons-material/LunchDiningRounded';
import RamenDiningRoundedIcon from '@mui/icons-material/RamenDiningRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

const fallbackItems = [
  'Savouries',
  'Vadam',
  'Sweets',
  'Snacks',
  'Meals',
  'Specials',
];

export function getCategoryPresentation(name) {
  const label = String(name || '').toLowerCase();

  if (label.includes('sweet') || label.includes('dessert')) {
    return {
      icon: <CookieRoundedIcon sx={{ fontSize: 30 }} />,
      color: '#ff7a66',
      halo: '#ffe1b0',
      shadow: 'rgba(244, 155, 74, 0.28)',
    };
  }

  if (label.includes('vadam') || label.includes('fryum')) {
    return {
      icon: <BakeryDiningRoundedIcon sx={{ fontSize: 30 }} />,
      color: '#f26d4f',
      halo: '#ffd46b',
      shadow: 'rgba(236, 162, 52, 0.3)',
    };
  }

  if (
    label.includes('savour') ||
    label.includes('savory') ||
    label.includes('snack') ||
    label.includes('papad')
  ) {
    return {
      icon: <FastfoodRoundedIcon sx={{ fontSize: 30 }} />,
      color: '#ff6e63',
      halo: '#ffd35e',
      shadow: 'rgba(233, 154, 41, 0.28)',
    };
  }

  if (label.includes('meal') || label.includes('rice')) {
    return {
      icon: <LunchDiningRoundedIcon sx={{ fontSize: 30 }} />,
      color: '#f86a60',
      halo: '#ffd56f',
      shadow: 'rgba(232, 167, 68, 0.3)',
    };
  }

  if (label.includes('combo') || label.includes('special')) {
    return {
      icon: <LocalDiningRoundedIcon sx={{ fontSize: 30 }} />,
      color: '#ff7265',
      halo: '#ffd468',
      shadow: 'rgba(235, 165, 60, 0.28)',
    };
  }

  if (label.includes('soup') || label.includes('ramen')) {
    return {
      icon: <RamenDiningRoundedIcon sx={{ fontSize: 30 }} />,
      color: '#f67357',
      halo: '#ffd971',
      shadow: 'rgba(240, 173, 74, 0.3)',
    };
  }

  return {
    icon: <StorefrontRoundedIcon sx={{ fontSize: 30 }} />,
    color: '#ff7565',
    halo: '#ffd56c',
    shadow: 'rgba(232, 168, 67, 0.28)',
  };
}

export default function CategorySlider({
  title,
  items = [],
  onCategoryClick,
  selectedCategoryId,
  onSeeAll,
}) {
  const categoryItems = items.length ? items : fallbackItems.map((name) => ({ name }));

  return (
    <>
      <Box
        sx={{
          px: 2,
          mt: 2.5,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant='subtitle1'
          noWrap
          sx={{
            fontWeight: '700',
            fontSize: '1.22rem',
            color: '#1c2430',
          }}
        >
          {title}
        </Typography>
        <Button
          onClick={onSeeAll}
          endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{
            minWidth: 'auto',
            p: 0,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#ff6d6d',
          }}
        >
          See All
        </Button>
      </Box>
      <Box
        component='section'
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          display: 'flex',
          gap: 1.5,
          px: 1.5,
          py: 0.75,
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-x',
          '&::-webkit-scrollbar': {
            display: 'none',
            height: 0,
          },
          scrollbarWidth: 'none',
          scrollSnapType: 'x proximity',
          '&:focus': {
            outline: 'none',
          },
        }}
        role='group'
        aria-label='categories scroller'
      >
        {categoryItems.map((item, idx) => {
          const isSelected =
            String(selectedCategoryId) === String(item.category_id);
          const presentation = getCategoryPresentation(item.name);

          return (
            <Button
              key={item.category_id || item.name || idx}
              variant='text'
              onClick={() => onCategoryClick?.(item)}
              sx={{
                scrollSnapAlign: 'start',
                flex: '0 0 auto',
                minWidth: 92,
                px: 0,
                py: 0.25,
                borderRadius: '24px',
                textTransform: 'none',
                display: 'grid',
                justifyItems: 'center',
                gap: 0.8,
                color: '#25303b',
                background: 'transparent',
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '999px',
                  background: `radial-gradient(circle at 30% 28%, #fff6bf 0%, ${presentation.halo} 40%, #f4bc37 100%)`,
                  color: presentation.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  boxShadow: isSelected
                    ? `0 14px 28px ${presentation.shadow}`
                    : `0 10px 18px ${presentation.shadow}`,
                  transform: isSelected ? 'translateY(-2px)' : 'none',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {presentation.icon}
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gap: 0.15, justifyItems: 'center' }}>
                <Typography
                  component='span'
                  sx={{
                    fontSize: '0.92rem',
                    lineHeight: 1.15,
                    fontWeight: isSelected ? 700 : 600,
                    maxWidth: 90,
                    textAlign: 'center',
                    color: isSelected ? '#1f2937' : '#3b4350',
                    whiteSpace: 'normal',
                  }}
                >
                  {item.name}
                </Typography>
                {item.product_count !== undefined ? (
                  <Typography
                    component='span'
                    sx={{
                      fontSize: '0.69rem',
                      lineHeight: 1,
                      color: isSelected ? '#ff6d6d' : '#8a9099',
                      fontWeight: 600,
                    }}
                  >
                    {item.product_count} items
                  </Typography>
                ) : null}
              </Box>
            </Button>
          );
        })}
      </Box>
    </>
  );
}
